import { documentFormatter } from "@/utils/documentFormatter";
import {
  Table,
  Thead,
  Tr,
  Th,
  Box,
  Tbody,
  Td,
  Text,
  Flex,
  Tooltip,
  useColorModeValue,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import { useState } from "react";
import {ReducedProposal } from "@/models/proposal.model";
import { Role } from "@/models/role.model";
import { PermissionEnum } from "@/utils/permissionEnum";
import {
  ProposalStatus,
  StatusRenderer,
} from "../ProposalTable/proposalStatusRenderer";
import { AiOutlineEye } from "react-icons/ai";
import ProposalModal from "../ProposalModal";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";

interface ProposalNewTableProps {
  proposals: ReducedProposal[];
  sortBy: string;
  sortDirection: string;
  setSortBy: (field: string) => void;
  setSortDirection: (field: string) => void;
}

export default function ProposalNewTable({
  proposals,
  setSortBy,
  setSortDirection,
  sortBy,
  sortDirection,
}: ProposalNewTableProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const backgroundStyle = useConditionalBackground();
    const [currentProposal, setCurrentProposal] = useState<ReducedProposal | null>(null);
  const authStorageString = localStorage.getItem("auth-storage");
  const authStorage = authStorageString ? JSON.parse(authStorageString) : null;
  const userRoles = authStorage?.state?.user?.roles || [];
  const userPermissions = userRoles.flatMap((role: Role) => role.permissions);
  const hasPermission =
    userPermissions.includes(PermissionEnum.GRANATECH) ||
    userPermissions.includes(PermissionEnum.ADMIN);

  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("teal.50", "teal.900");
  const rowBorderColor = useColorModeValue("gray.200", "gray.600");

  const handleSort = (field: string) => {
    setSortBy(field);
    setSortDirection(
      sortBy === field && sortDirection === "asc" ? "desc" : "asc"
    );
  };

  return (
    <>
      <Box>
        <Box overflow="auto" borderRadius="md" boxShadow="md">
          <Table w={"100%"} size="sm">
            <Thead
              {...backgroundStyle}
              position="sticky"
              top={0}
              zIndex={1}
              boxShadow="md"
            >
              <Tr>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  Ações
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>NOME DO CLIENTE</Text>
                    <IconButton
                      icon={
                        sortBy === "id" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by name"
                      bg={"gray.200"}
                      onClick={() => handleSort("id")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>CPF DO CLIENTE</Text>
                    <IconButton
                      icon={
                        sortBy === "document" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by document"
                      bg={"gray.200"}
                      onClick={() => handleSort("document")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>CORBAN</Text>
                    <IconButton
                      icon={
                        sortBy === "corban" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by corban"
                      bg={"gray.200"}
                      onClick={() => handleSort("corban")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>PROMOTORA</Text>
                    <IconButton
                      icon={
                        sortBy === "promoter" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by promoter"
                      bg={"gray.200"}
                      onClick={() => handleSort("promoter")}
                    />
                  </HStack>
                </Th>
                {hasPermission && (
                  <Th
                    color={headerColor}
                    textAlign="center"
                    fontSize="xs"
                    py={5}
                  >
                    <HStack justify="center">
                      <Text>VENDEDOR</Text>
                      <IconButton
                        icon={
                          sortBy === "seller" && sortDirection === "asc" ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        }
                        variant="ghost"
                        size="1"
                        fontSize={"1rem"}
                        borderRadius={"lg"}
                        aria-label="Sort by seller"
                        bg={"gray.200"}
                        onClick={() => handleSort("seller")}
                      />
                    </HStack>
                  </Th>
                )}

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>TABELA</Text>
                    <IconButton
                      icon={
                        sortBy === "product" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by product"
                      bg={"gray.200"}
                      onClick={() => handleSort("product")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>VALOR LIBERADO</Text>
                    <IconButton
                      icon={
                        sortBy === "releasedAmount" &&
                        sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by releasedAmount"
                      bg={"gray.200"}
                      onClick={() => handleSort("releasedAmount")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>DATA DA PROPOSTA</Text>
                    <IconButton
                      icon={
                        sortBy === "operationDate" &&
                        sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by operationDate"
                      bg={"gray.200"}
                      onClick={() => handleSort("operationDate")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>DATA DA FORMALIZAÇÃO</Text>
                    <IconButton
                      icon={
                        sortBy === "lastFormalizationDate" &&
                        sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by lastFormalizationDate"
                      bg={"gray.200"}
                      onClick={() => handleSort("lastFormalizationDate")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>DATA DO PGTO</Text>
                    <IconButton
                      icon={
                        sortBy === "disbursementDate" &&
                        sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by disbursementDate"
                      bg={"gray.200"}
                      onClick={() => handleSort("disbursementDate")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>STATUS DA PROPOSTA</Text>
                    <IconButton
                      icon={
                        sortBy === "status" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by status"
                      bg={"gray.200"}
                      onClick={() => handleSort("status")}
                    />
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {proposals?.map((proposal, index) => (
                <Tr
                  key={index}
                  _hover={{ bg: rowHoverBg }}
                  transition="all 0.3s ease-in-out"
                  borderBottom="1px solid"
                  borderColor={rowBorderColor}
                >
                  <Td textAlign="center" fontSize="sm" py={3}>
                    <Tooltip label="Visualizar" aria-label="Visualizar Tooltip" placement="top">
                      <Flex
                        justify="center"
                        align="center"
                        cursor="pointer"
                        onClick={() => {
                          setCurrentProposal(proposal);
                          setIsModalOpen(true);
                        }}
                      >
                        <AiOutlineEye fontSize="1.5rem" color="teal.500" />
                      </Flex>
                    </Tooltip>
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal.naturalPersonName || "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal.naturalPersonDocument
                      ? documentFormatter(proposal.naturalPersonDocument)
                      : "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal.corbanBusinessName || "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal.creditPromoterBusinessName|| "-"}
                  </Td>
                  {hasPermission && (
                    <Td textAlign="center" fontSize="sm" py={3}>
                      {`${proposal?.sellerName} ${proposal?.sellerLastName}` ||
                        "-"}
                    </Td>
                  )}
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal.financialProductName || "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {BrlCurrencyFormatter(
                      proposal.financialReleasedAmount
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal?.naturalPersonUpdatedAt ? (
                      <>
                        <Box>
                          {new Date(
                            proposal.naturalPersonUpdatedAt
                          ).toLocaleDateString("pt-BR")}
                        </Box>
                        <Box fontSize="xs">
                          {new Date(
                            proposal.naturalPersonUpdatedAt
                          ).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Box>
                      </>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal?.unicoLastFormalizationDate ? (
                      <>
                        <Box>
                          {new Date(
                            proposal.unicoLastFormalizationDate
                          ).toLocaleDateString("pt-BR")}
                        </Box>
                        <Box fontSize="xs">
                          {new Date(
                            proposal.unicoLastFormalizationDate
                          ).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Box>
                      </>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal?.disbursementDate ? (
                      <>
                        <Box>
                          {new Date(
                            proposal.disbursementDate
                          ).toLocaleDateString("pt-BR")}
                        </Box>
                        <Box fontSize="xs">
                          {new Date(
                            proposal.disbursementDate
                          ).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Box>
                      </>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    <StatusRenderer
                      status={proposal.status as ProposalStatus}
                      customStatus={proposal.customStatus}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      <ProposalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        proposalId={currentProposal?.id!}
      />
    </>
  );
}
